import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stepper,
  Step,
  StepLabel,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Modal,
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  ShoppingBag as ShoppingBagIcon,
  LocalShipping as ShippingIcon,
  Store as StoreIcon,
  Info,
  CheckCircle,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { cartApi } from '../api/cart';
import { checkoutApi } from '../api/checkout';
import checkoutService from '../services/checkoutService';
import { clearCart } from '../../features/cartSlice';
import PaymentForm from '../../components/customer/PaymentForm';
import loadingIcon from '../../images/loading.svg';
import Seo from '../components/Seo';

// TODO: make merchant-configurable
const FREE_SHIPPING_THRESHOLD = 5000;

const Checkout = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0); // Start directly on Delivery Information step
  const [paymentMethod, setPaymentMethod] = useState('mpesa');
  const cartItems = cartApi.getCart();
  const cartTotal = cartApi.getCartTotal();

  // Delivery method state
  const [deliveryMethod, setDeliveryMethod] = useState('ship'); // 'ship' or 'pickup'

  // Delivery location states
  const [selectedArea, setSelectedArea] = useState('');
  const [deliveryFee, setDeliveryFee] = useState(0);

  // Pickup location states
  const [pickupLocations, setPickupLocations] = useState([]);
  const [selectedPickupLocation, setSelectedPickupLocation] = useState('');
  const [loadingPickupLocations, setLoadingPickupLocations] = useState(true);

  // Shipping regions from API
  const [shippingRegions, setShippingRegions] = useState([]);
  const [selectedShippingRegion, setSelectedShippingRegion] = useState('');
  const [loadingShippingRegions, setLoadingShippingRegions] = useState(true);

  // Payment methods state
  const [, setPaymentMethods] = useState({
    mpesa: false,
    card: false,
    paybill: false,
  });
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);

  const [shippingAddress, setShippingAddress] = useState({
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'Kenya',
  });

  const [, setBillingAddress] = useState({
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'Kenya',
  });

  const [sameAsShipping] = useState(true);

  // Form errors
  const [formErrors, setFormErrors] = useState({});

  // Payment processing state
  const [orderCreated, setOrderCreated] = useState(false);
  const [orderCreatedDetails, setOrderCreatedDetails] = useState({});
  const [openOrderProcessing, setOpenOrderProcessing] = useState(false);
  const [isCardPaymentLoading, setIsCardPaymentLoading] = useState(false);
  const [, setCardPaymentStatus] = useState('');
  const [mpesaPhoneNumber, setMpesaPhoneNumber] = useState('');
  const [mobileMoneyNumber, setMobileMoneyNumber] = useState('');
  const [paymentMethodsStatus, setPaymentMethodsStatus] = useState({
    mpesa: false,
    card: false,
    paybill: false,
    momo: false
  });
  const [loadingPaymentStatus, setLoadingPaymentStatus] = useState(false);

  // Alerts
  const [successAlert, setSuccessAlert] = useState({ show: false, message: '' });
  const [errorAlert, setErrorAlert] = useState({ show: false, message: '' });

  // Payment status tracking
  const [, setPaymentInitiated] = useState(false);
  const [paymentSuccessful, setPaymentSuccessful] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [paymentStatusDetails, setPaymentStatusDetails] = useState(null);

  // Customer information with more fields from old checkout
  const [customerInfo, setCustomerInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    mpesaPhone: '',
    apartment: '',
    address: '',
    city: '',
    county: '',
    postalCode: ''
  });

  const dispatch = useDispatch();
  const base_url = process.env.REACT_APP_WEB_SOCKET_URL;
  const { businessName } = useSelector((state) => state.configs);

  // Fetch shipping regions, pickup locations and customer profile
  useEffect(() => {
    fetchCustomerProfile();
    fetchShippingRegions();
    fetchPickupLocations();
    fetchPaymentMethods();
  }, []);

  // Fetch data based on delivery method change
  useEffect(() => {
    if (deliveryMethod === 'ship') {
      if (shippingRegions.length === 0) {
        fetchShippingRegions();
      }
    } else if (deliveryMethod === 'pickup') {
      if (pickupLocations.length === 0) {
        fetchPickupLocations();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryMethod]);

  const fetchShippingRegions = async () => {
    setLoadingShippingRegions(true);
    try {
      const regions = await checkoutApi.getShippingRegions();
      const formattedRegions = checkoutService.formatShippingRegions(regions);
      setShippingRegions(formattedRegions);
    } catch (error) {
      console.error('Error fetching shipping regions:', error);
    } finally {
      setLoadingShippingRegions(false);
    }
  };

  const fetchPickupLocations = async () => {
    setLoadingPickupLocations(true);
    try {
      const locations = await checkoutApi.getPickupLocations();
      const formattedLocations = checkoutService.formatPickupLocations(locations);
      setPickupLocations(formattedLocations);
    } catch (error) {
      console.error('Error fetching pickup locations:', error);
    } finally {
      setLoadingPickupLocations(false);
    }
  };

  const fetchCustomerProfile = async () => {
    try {
      const customerData = await checkoutApi.getCustomerProfile();
      if (customerData) {
        setCustomerInfo({
          firstName: customerData?.firstName || '',
          lastName: customerData?.lastName || '',
          email: customerData?.email || '',
          phone: customerData?.phone || '',
          mpesaPhone: customerData?.phone || '',
          apartment: customerData?.customerpreference?.apartment || '',
          address: customerData?.customerpreference?.address || '',
          city: customerData?.customerpreference?.city || '',
          county: customerData?.customerpreference?.county || '',
          postalCode: customerData?.customerpreference?.postalCode || ''
        });

        // Also update shipping address
        setShippingAddress({
          street: customerData?.customerpreference?.address || '',
          city: customerData?.customerpreference?.city || '',
          state: customerData?.customerpreference?.county || '',
          zipCode: customerData?.customerpreference?.postalCode || '',
          country: 'Kenya',
        });

        // Set area if exists
        if (customerData?.customerpreference?.city) {
          setSelectedArea(customerData.customerpreference.city);
        }
      }
    } catch (error) {
      console.error('Error fetching customer profile:', error);
    }
  };

  const fetchPaymentMethods = async () => {
    setLoadingPaymentMethods(true);
    try {
      const result = await checkoutService.fetchPaymentMethods();
      if (result.success) {
        setPaymentMethods(result.methods);
        setPaymentMethodsStatus(result.methods);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  const fetchPaymentStatusDetails = async () => {
    if (!orderCreatedDetails?._id) {
      return;
    }

    setLoadingPaymentStatus(true);
    try {
      const result = await checkoutService.checkPaymentStatus(
        orderCreatedDetails._id,
        orderCreatedDetails.businessId
      );

      if (result.success) {
        setPaymentStatusDetails(result);
      } else {
        console.warn('Payment status check failed:', result.message);
      }
    } catch (error) {
      console.error('Error fetching payment status:', error);
    } finally {
      setLoadingPaymentStatus(false);
    }
  };

  // Fetch payment status details when moving to review step and payment successful
  useEffect(() => {
    if (activeStep === 2 && paymentSuccessful && !paymentStatusDetails) {
      fetchPaymentStatusDetails();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, paymentSuccessful, paymentStatusDetails]);

  // Helper function to safely access payment status data from multiple possible structures
  const getPaymentStatusData = (key, defaultValue = 'N/A') => {
    if (!paymentStatusDetails) {
      console.debug(`[getPaymentStatusData] paymentStatusDetails is null/undefined for key: ${key}`);
      return defaultValue;
    }

    // Try multiple possible locations for the data
    // 1. Direct property on paymentStatusDetails
    if (paymentStatusDetails[key] !== undefined && paymentStatusDetails[key] !== null) {
      console.debug(`[getPaymentStatusData] Found '${key}' at top level: ${JSON.stringify(paymentStatusDetails[key]).substring(0, 50)}`);
      return paymentStatusDetails[key];
    }

    // 2. Nested under 'order' property
    if (paymentStatusDetails.order && paymentStatusDetails.order[key] !== undefined && paymentStatusDetails.order[key] !== null) {
      console.debug(`[getPaymentStatusData] Found '${key}' in order: ${JSON.stringify(paymentStatusDetails.order[key]).substring(0, 50)}`);
      return paymentStatusDetails.order[key];
    }

    // 3. Nested under 'data' property
    if (paymentStatusDetails.data && paymentStatusDetails.data[key] !== undefined && paymentStatusDetails.data[key] !== null) {
      console.debug(`[getPaymentStatusData] Found '${key}' in data: ${JSON.stringify(paymentStatusDetails.data[key]).substring(0, 50)}`);
      return paymentStatusDetails.data[key];
    }

    console.debug(`[getPaymentStatusData] '${key}' not found anywhere, returning default: ${defaultValue}`);
    return defaultValue;
  };

  // Update city when area changes
  useEffect(() => {
    if (selectedArea) {
      setShippingAddress(prev => ({ ...prev, city: selectedArea }));
    }
  }, [selectedArea]);

  const steps = ['Delivery Information', 'Payment Method', 'Review Order'];

  const handleNext = async () => {
    // Validate based on current step
    if (activeStep === 0) {
      // Validate delivery information (step 0)
      const formData = {
        firstName: customerInfo.firstName,
        lastName: customerInfo.lastName,
        email: customerInfo.email,
        phone: customerInfo.phone,
        address: customerInfo.address || shippingAddress.street,
        city: customerInfo.city || selectedArea,
        county: customerInfo.county,
        postalCode: customerInfo.postalCode || shippingAddress.zipCode,
        area: selectedArea,
        paymentMethod: paymentMethod,
        mpesaPhone: customerInfo.mpesaPhone,
        pickupLocationId: selectedPickupLocation,
        shippingRegionId: selectedShippingRegion
      };

      const validation = checkoutService.validateCheckoutForm(formData, deliveryMethod);
      if (!validation.isValid) {
        setFormErrors(validation.errors);
        return;
      }

      // Clear errors and proceed to payment method step
      setFormErrors({});
      setActiveStep((prevStep) => prevStep + 1);
    } else if (activeStep === 1) {
      // Payment method step -> Review step
      // Order is already created when user selected the payment method

      if (!paymentMethod) {
        setErrorAlert({ show: true, message: 'Please select a payment method' });
        return;
      }

      if (!orderCreated) {
        setErrorAlert({ show: true, message: 'Order not created. Please select a payment method and try again.' });
        return;
      }

      // Proceed to review step
      setErrorAlert({ show: false, message: '' });
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handlePaymentMethodChange = async (newPaymentMethod) => {
    // Update payment method
    setPaymentMethod(newPaymentMethod);

    // Reset payment success state when changing payment method
    setPaymentSuccessful(false);

    // Create order when payment method is selected
    // Phone number validation will happen when user clicks "Pay Now"
    await createOrderForPayment(newPaymentMethod);
  };

  const handleInitiateStkPayment = async (method) => {
    // Use provided method or fall back to current paymentMethod
    const selectedMethod = method || paymentMethod;

    // Validate phone number
    if (selectedMethod === 'mpesa' && !mpesaPhoneNumber) {
      setErrorAlert({ show: true, message: 'Please enter M-Pesa phone number' });
      return;
    }
    if (selectedMethod === 'kcbpaybill' && !mobileMoneyNumber) {
      setErrorAlert({ show: true, message: 'Please enter Mobile Money phone number' });
      return;
    }

    if (!orderCreated) {
      setErrorAlert({ show: true, message: 'Order not created. Please try again.' });
      return;
    }

    setIsPaymentProcessing(true);
    setPaymentInitiated(true);
    setOpenOrderProcessing(true);

    const callbacks = {
      onSuccess: (message) => {
        setIsPaymentProcessing(false);
        setPaymentSuccessful(true);
        setOpenOrderProcessing(false);

        setSuccessAlert({ show: true, message: message || 'Your order has been processed successfully.' });

        // Navigate to order review step (step 2) after 2 seconds to show payment success
        setTimeout(() => {
          setActiveStep(2);
        }, 2000);
      },
      onError: (message) => {
        setIsPaymentProcessing(false);
        setPaymentInitiated(false);
        setOpenOrderProcessing(false);
        setErrorAlert({ show: true, message });
      },
      onInfo: (message) => {
        // Show payment processing info updates (status "OK" from WebSocket)
        setSuccessAlert({ show: true, message: message || 'Payment processing...' });
      },
      onTimeout: (message) => {
        setIsPaymentProcessing(false);
        setPaymentInitiated(false);
        setOpenOrderProcessing(false);
        setErrorAlert({ show: true, message });
      }
    };

    await checkoutService.initiateStkPush(
      orderCreatedDetails,
      selectedMethod,
      selectedMethod === 'mpesa' ? mpesaPhoneNumber : mobileMoneyNumber,
      base_url,
      callbacks
    );
  };

  const createOrderForPayment = async (selectedPaymentMethod) => {
    // Don't create order again if already created
    if (orderCreated) {
      return;
    }

    try {
      const formData = {
        firstName: customerInfo.firstName,
        lastName: customerInfo.lastName,
        email: customerInfo.email,
        phone: customerInfo.phone,
        address: customerInfo.address || shippingAddress.street,
        city: customerInfo.city || selectedArea,
        county: customerInfo.county,
        postalCode: customerInfo.postalCode || shippingAddress.zipCode,
        area: selectedArea,
        paymentMethod: selectedPaymentMethod,
        mpesaPhone: mpesaPhoneNumber,
        mobileMoneyNumber: mobileMoneyNumber,
        pickupLocationId: selectedPickupLocation,
        shippingRegionId: selectedShippingRegion
      };

      const payload = checkoutService.prepareOrderPayload(formData, cartItems, deliveryMethod);

      const orderResult = await checkoutApi.createOrder(payload);

      if (orderResult?.success) {
        const orderData = orderResult.data;
        setOrderCreated(true);
        setOrderCreatedDetails(orderData);
        setErrorAlert({ show: false, message: '' });
        setSuccessAlert({ show: true, message: 'Order created successfully! Now enter your payment details.' });
      } else {
        console.error('Order creation failed:', orderResult);
        setErrorAlert({ show: true, message: orderResult?.message || 'Failed to create order. Please try again.' });
        setPaymentMethod(''); // Reset payment method on failure
      }
    } catch (error) {
      console.error('Error creating order:', error);
      setErrorAlert({ show: true, message: 'An error occurred while creating your order.' });
      setPaymentMethod(''); // Reset payment method on failure
    }
  };

  const handleCardPaymentResponse = (status) => {
    setCardPaymentStatus(status);
    if (status === 'SUCCESS') {
      setOpenOrderProcessing(false);
      setPaymentSuccessful(true);
      setSuccessAlert({ show: true, message: 'Payment processed successfully! Your order has been placed.' });
      // Cart will be cleared when user navigates away from checkout
    } else if (status === 'FAILED') {
      setOpenOrderProcessing(false);
      setErrorAlert({ show: true, message: 'Payment failed. Please try again.' });
    }
  };

  const handlePlaceOrder = async () => {
    try {
      // Order is already created at payment method step
      // On review step, we just initiate payment
      if (!orderCreated) {
        setErrorAlert({ show: true, message: 'Order data missing. Please go back and try again.' });
        return;
      }

      // Initiate payment based on payment method
      if (paymentMethod === 'card') {
        // Card payment is handled by PaymentForm component
        // It will call handleCardPaymentResponse when complete
        return;
      } else {
        // For STK push methods (M-Pesa, KCB Paybill)
        setOpenOrderProcessing(true);

        const callbacks = {
          onSuccess: () => {
            setOpenOrderProcessing(false);
            setSuccessAlert({ show: true, message: 'Payment successful! Redirecting to order confirmation...' });
            dispatch(clearCart());
            cartApi.clearCart();
            setTimeout(() => {
              navigate(`/order-confirmation`, { state: { order: orderCreatedDetails } });
            }, 2000);
          },
          onError: (message) => {
            setOpenOrderProcessing(false);
            setErrorAlert({ show: true, message });
          },
          onInfo: (message) => {
            setSuccessAlert({ show: true, message });
          },
          onTimeout: (message) => {
            setOpenOrderProcessing(false);
            setErrorAlert({ show: true, message });
          }
        };

        await checkoutService.initiateStkPush(
          orderCreatedDetails,
          paymentMethod,
          paymentMethod === 'mpesa' ? mpesaPhoneNumber : mobileMoneyNumber,
          base_url,
          callbacks
        );
      }
    } catch (error) {
      console.error('Error initiating payment:', error);
      setErrorAlert({ show: true, message: 'An error occurred while processing your payment. Please try again.' });
    }
  };

  const handleAddressChange = (field, value, isBilling = false) => {
    if (isBilling) {
      setBillingAddress((prev) => ({ ...prev, [field]: value }));
    } else {
      setShippingAddress((prev) => ({ ...prev, [field]: value }));
      if (sameAsShipping) {
        setBillingAddress((prev) => ({ ...prev, [field]: value }));
      }
    }
  };

  const renderDeliveryForm = () => {
    const hasNoDeliveryOptions = !loadingShippingRegions && !loadingPickupLocations &&
      shippingRegions.length === 0 && pickupLocations.length === 0;

    return (
      <Box>
        {/* Customer Information Section - Improved Design */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{
            mb: 3,
            fontWeight: 600,
            color: '#212B36',
            fontSize: '18px'
          }}>
            Customer Information
          </Typography>

          <Box sx={{
            border: '1px solid #e0e0e0',
            borderRadius: '6px',
            p: 2,
            backgroundColor: '#fff',
          }}>
            <Typography variant="caption" sx={{
              color: '#666',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              display: 'block',
              mb: 1.5
            }}>
              Contact Details
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2.5 }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1.5 }}>
                <TextField
                  fullWidth
                  required
                  label="First Name"
                  value={customerInfo.firstName}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, firstName: e.target.value })}
                  error={!!formErrors.firstName}
                  helperText={formErrors.firstName}
                  placeholder="Enter first name"
                  size="small"
                  variant="outlined"
                  sx={{
                    backgroundColor: '#fafafa',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                    }
                  }}
                />
                <TextField
                  fullWidth
                  required
                  label="Last Name"
                  value={customerInfo.lastName}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, lastName: e.target.value })}
                  error={!!formErrors.lastName}
                  helperText={formErrors.lastName}
                  placeholder="Enter last name"
                  size="small"
                  variant="outlined"
                  sx={{
                    backgroundColor: '#fafafa',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                    }
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1.5 }}>
                <TextField
                  fullWidth
                  required
                  type="email"
                  label="Email Address"
                  value={customerInfo.email}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                  placeholder="your.email@example.com"
                  size="small"
                  variant="outlined"
                  sx={{
                    backgroundColor: '#fafafa',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                    }
                  }}
                />
                <TextField
                  fullWidth
                  required
                  label="Phone Number"
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                  error={!!formErrors.phone}
                  helperText={formErrors.phone}
                  placeholder="+254 7XX XXX XXX"
                  size="small"
                  variant="outlined"
                  sx={{
                    backgroundColor: '#fafafa',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                    }
                  }}
                />
              </Box>
            </Box>

            {/* Info Note */}
            <Box sx={{
              mt: 2.5,
              p: 1.5,
              backgroundColor: '#F0FFFE',
              borderRadius: '8px',
              border: '1px solid #B0E2E0'
            }}>
              <Typography variant="caption" sx={{ color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Info sx={{ fontSize: 16 }} />
                We'll use this information to send you order updates and delivery notifications
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Shipping Options Section */}
        {hasNoDeliveryOptions ? (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Shipping Options
            </Typography>
            <Alert severity="warning">
              No delivery options are currently available. Please contact the store for assistance.
            </Alert>
          </Box>
        ) : (
        <>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Shipping Options
          </Typography>

          {/* Free Shipping Alert */}
          {cartTotal >= FREE_SHIPPING_THRESHOLD && (
            <Alert
              severity="success"
              icon={<ShippingIcon />}
              sx={{ mb: 2 }}
            >
              Congratulations! You qualify for FREE standard shipping!
            </Alert>
          )}

          {/* Delivery Method Cards */}
          <RadioGroup
            value={deliveryMethod}
            onChange={(e) => setDeliveryMethod(e.target.value)}
          >
            {/* Store Pickup Card */}
            <Card
              sx={{
                mb: 2,
                border: '2px solid',
                borderColor: deliveryMethod === 'pickup' ? 'var(--primary)' : '#e0e0e0',
                cursor: 'pointer',
                transition: 'all 0.2s',
                backgroundColor: deliveryMethod === 'pickup' ? 'color-mix(in srgb, var(--primary) 4%, transparent)' : '#fff',
                '&:hover': {
                  borderColor: 'var(--primary)',
                  backgroundColor: 'color-mix(in srgb, var(--primary) 4%, transparent)',
                },
              }}
              onClick={() => setDeliveryMethod('pickup')}
            >
              <CardContent sx={{ p: 2.5 }}>
                <FormControlLabel
                  value="pickup"
                  control={
                    <Radio
                      sx={{
                        color: 'var(--primary)',
                        '&.Mui-checked': {
                          color: 'var(--primary)',
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ width: '100%', ml: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
                        <StoreIcon sx={{ color: deliveryMethod === 'pickup' ? 'var(--primary)' : '#666', flexShrink: 0 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Store Pickup
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Pick up from our store
                          </Typography>
                          <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                              label="FREE pickup"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                backgroundColor: 'color-mix(in srgb, var(--primary) 10%, transparent)',
                                color: 'var(--primary)',
                              }}
                            />
                            <Chip
                              label="Ready in 2-4 hours"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                backgroundColor: 'color-mix(in srgb, var(--primary) 10%, transparent)',
                                color: 'var(--primary)',
                              }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: '#4caf50',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            FREE
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'nowrap' }}>
                            Ready in 2-4 hours
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  }
                  sx={{ m: 0, width: '100%' }}
                />
              </CardContent>
            </Card>

            {/* Standard Delivery Card */}
            <Card
              sx={{
                mb: 2,
                border: '2px solid',
                borderColor: deliveryMethod === 'ship' ? 'var(--primary)' : '#e0e0e0',
                cursor: 'pointer',
                transition: 'all 0.2s',
                backgroundColor: deliveryMethod === 'ship' ? 'color-mix(in srgb, var(--primary) 4%, transparent)' : '#fff',
                '&:hover': {
                  borderColor: 'var(--primary)',
                  backgroundColor: 'color-mix(in srgb, var(--primary) 4%, transparent)',
                },
              }}
              onClick={() => setDeliveryMethod('ship')}
            >
              <CardContent sx={{ p: 2.5 }}>
                <FormControlLabel
                  value="ship"
                  control={
                    <Radio
                      sx={{
                        color: 'var(--primary)',
                        '&.Mui-checked': {
                          color: 'var(--primary)',
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ width: '100%', ml: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
                        <ShippingIcon sx={{ color: deliveryMethod === 'ship' ? 'var(--primary)' : '#666', flexShrink: 0 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Standard Delivery
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Delivered within 5-7 business days
                          </Typography>
                          <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                              label="Nationwide coverage"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                backgroundColor: 'color-mix(in srgb, var(--primary) 10%, transparent)',
                                color: 'var(--primary)',
                              }}
                            />
                            <Chip
                              label="SMS notifications"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                backgroundColor: 'color-mix(in srgb, var(--primary) 10%, transparent)',
                                color: 'var(--primary)',
                              }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: cartTotal >= FREE_SHIPPING_THRESHOLD ? '#4caf50' : '#000',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              fontSize: { xs: '0.9rem', sm: 'h6.fontSize' }
                            }}
                          >
                            {!selectedShippingRegion ? 'Select Shipping Region' :
                             cartTotal >= FREE_SHIPPING_THRESHOLD ? 'FREE' :
                             `KSH ${deliveryFee.toLocaleString()}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'nowrap' }}>
                            5-7 business days
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  }
                  sx={{ m: 0, width: '100%' }}
                />
              </CardContent>
            </Card>
          </RadioGroup>
        </Box>

        {/* Conditional Rendering based on delivery method */}
        {deliveryMethod === 'ship' ? (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#333' }}>
              Delivery Information
            </Typography>
            {renderDeliveryFields()}
          </Box>
        ) : (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#333' }}>
              Store Pickup Details
            </Typography>
            {renderPickupFields()}
          </Box>
        )}
        </>
        )}
      </Box>
    );
  };

  const renderDeliveryFields = () => {
    return (
      <Box sx={{
        border: '1px solid #e0e0e0',
        borderRadius: '6px',
        p: 2,
        backgroundColor: '#fff',
      }}>
        {/* Shipping Address Section Header */}
        <Typography variant="caption" sx={{
          color: '#666',
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          display: 'block',
          mb: 1.5
        }}>
          Shipping Address
        </Typography>

        {/* Shipping Region - Full width */}
        {loadingShippingRegions ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, mb: 2.5 }}>
            <CircularProgress size={24} sx={{ color: 'var(--primary)' }} />
          </Box>
        ) : (
          <Box sx={{ mb: 2.5 }}>
            <TextField
              select
              fullWidth
              required
              label="Shipping Region"
              value={selectedShippingRegion || ''}
              onChange={(e) => {
                setSelectedShippingRegion(e.target.value);
                setSelectedArea('');
                const region = shippingRegions.find(r => r._id === e.target.value);
                if (region) {
                  setDeliveryFee(cartTotal >= FREE_SHIPPING_THRESHOLD ? 0 : region.shippingCost || 0);
                }
              }}
              error={!!formErrors.shippingRegion}
              helperText={formErrors.shippingRegion}
              size="small"
              variant="outlined"
              sx={{
                backgroundColor: '#fafafa',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  borderColor: formErrors.shippingRegion ? '#f44336' : 'var(--primary)',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: formErrors.shippingRegion ? '#f44336' : 'var(--primary)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: formErrors.shippingRegion ? '#f44336' : 'var(--primary)',
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: formErrors.shippingRegion ? '#f44336' : 'var(--primary)',
                }
              }}
            >
              <MenuItem value="">
                <em style={{ color: '#999' }}>Select shipping region</em>
              </MenuItem>
              {shippingRegions.map((region) => (
                <MenuItem key={region._id} value={region._id}>
                  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{region.regionName}</span>
                    <span style={{ color: 'var(--primary)', fontWeight: 600, marginLeft: '16px' }}>
                      {cartTotal >= FREE_SHIPPING_THRESHOLD ? 'FREE' : `KSH ${region.shippingCost?.toLocaleString()}`}
                    </span>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {/* Area Select - ONLY SHOW when the selected region defines specific areas */}
        {selectedShippingRegion && (() => {
          const selectedRegionForAreas = shippingRegions.find(r => r._id === selectedShippingRegion);
          const regionAreas = selectedRegionForAreas?.areas || [];
          if (regionAreas.length === 0) return null;

          return (
            <Box sx={{ mb: 2.5 }}>
              <TextField
                select
                fullWidth
                label="Area"
                value={selectedArea || ''}
                onChange={(e) => setSelectedArea(e.target.value)}
                size="small"
                variant="outlined"
                sx={{
                  backgroundColor: '#fafafa',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                  }
                }}
              >
                <MenuItem value="">
                  <em style={{ color: '#999' }}>Select area (optional)</em>
                </MenuItem>
                {regionAreas.map((area) => {
                  const areaValue = typeof area === 'string' ? area : (area.name || area.areaName || area._id);
                  const areaLabel = typeof area === 'string' ? area : (area.name || area.areaName || areaValue);
                  return (
                    <MenuItem key={areaValue} value={areaValue}>
                      {areaLabel}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Box>
          );
        })()}

        {/* Address Fields - ONLY SHOW when shipping region is selected - TEMPLATE STYLING */}
        {selectedShippingRegion && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Row 2: Physical Address and City - RESPONSIVE LAYOUT */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1.5 }}>
              <TextField
                fullWidth
                required
                label="Physical Address"
                placeholder="House number, street name"
                value={customerInfo.address || shippingAddress.street}
                onChange={(e) => {
                  handleAddressChange('street', e.target.value);
                  setCustomerInfo({ ...customerInfo, address: e.target.value });
                }}
                error={!!formErrors.address}
                helperText={formErrors.address}
                size="small"
                variant="outlined"
                sx={{
                  backgroundColor: '#fafafa',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    borderColor: formErrors.address ? '#f44336' : 'inherit',
                  }
                }}
              />
              <TextField
                fullWidth
                required
                label="City"
                placeholder="e.g. Nairobi, Mombasa"
                value={customerInfo.city}
                onChange={(e) => setCustomerInfo({ ...customerInfo, city: e.target.value })}
                error={!!formErrors.city}
                helperText={formErrors.city}
                size="small"
                variant="outlined"
                sx={{
                  backgroundColor: '#fafafa',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    borderColor: formErrors.city ? '#f44336' : 'inherit',
                  }
                }}
              />
            </Box>

            {/* Row 3: County, Apartment, Postal Code - RESPONSIVE LAYOUT */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1.5 }}>
              <TextField
                fullWidth
                required
                label="County"
                placeholder="e.g. Nairobi County"
                value={customerInfo.county}
                onChange={(e) => setCustomerInfo({ ...customerInfo, county: e.target.value })}
                error={!!formErrors.county}
                helperText={formErrors.county}
                size="small"
                variant="outlined"
                sx={{
                  backgroundColor: '#fafafa',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    borderColor: formErrors.county ? '#f44336' : 'inherit',
                  },
                  flex: { md: 1 }
                }}
              />
              <TextField
                fullWidth
                label="Apartment/Suite (Optional)"
                placeholder="Apt, Suite, Unit, etc."
                value={customerInfo.apartment}
                onChange={(e) => setCustomerInfo({ ...customerInfo, apartment: e.target.value })}
                size="small"
                variant="outlined"
                sx={{
                  backgroundColor: '#fafafa',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                  },
                  flex: { md: 1 }
                }}
              />
              <TextField
                fullWidth
                required
                label="Postal Code"
                placeholder="00100"
                value={customerInfo.postalCode || shippingAddress.zipCode}
                onChange={(e) => {
                  handleAddressChange('zipCode', e.target.value);
                  setCustomerInfo({ ...customerInfo, postalCode: e.target.value });
                }}
                error={!!formErrors.postalCode}
                helperText={formErrors.postalCode}
                size="small"
                variant="outlined"
                sx={{
                  backgroundColor: '#fafafa',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    borderColor: formErrors.postalCode ? '#f44336' : 'inherit',
                  },
                  flex: { md: 1 }
                }}
              />
            </Box>
          </Box>
        )}

        {/* Delivery Fee Information */}
        {selectedShippingRegion && shippingRegions.length > 0 && (
          <Alert
            severity={cartTotal >= FREE_SHIPPING_THRESHOLD ? "success" : "info"}
            icon={<ShippingIcon />}
            sx={{
              mt: 2,
              borderRadius: '8px',
              backgroundColor: cartTotal >= FREE_SHIPPING_THRESHOLD ? '#F0FFF4' : '#F0FFFE',
              border: `1px solid ${cartTotal >= FREE_SHIPPING_THRESHOLD ? '#86EFAC' : '#B0E2E0'}`
            }}
          >
            {(() => {
              const selectedRegion = shippingRegions.find(r => r._id === selectedShippingRegion);
              if (!selectedRegion) return null;
              return (
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ color: cartTotal >= FREE_SHIPPING_THRESHOLD ? '#16A34A' : 'var(--primary)' }}>
                    Delivery to {selectedRegion.regionName}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#637381', mt: 0.5 }}>
                    Estimated delivery: 5-7 business days
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Typography variant="caption" sx={{ color: '#637381' }}>
                      Delivery Fee:
                    </Typography>
                    <Typography variant="subtitle1" sx={{ color: cartTotal >= FREE_SHIPPING_THRESHOLD ? '#16A34A' : 'var(--primary)', fontWeight: 600 }}>
                      {cartTotal >= FREE_SHIPPING_THRESHOLD ? 'FREE' : `KSH ${selectedRegion.shippingCost?.toLocaleString()}`}
                    </Typography>
                  </Box>
                </Box>
              );
            })()}
          </Alert>
        )}

        {/* Important Notice */}
        <Box sx={{
          mt: 2,
          p: 1.5,
          backgroundColor: '#FEF3C7',
          borderRadius: '8px',
          border: '1px solid #FDE68A'
        }}>
          <Typography variant="caption" sx={{ color: '#92400E' }}>
            <strong>📍 Important:</strong> Please ensure your delivery address and phone number are correct. Our delivery partner will contact you before delivery.
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderPickupFields = () => {
    return (
      <Box sx={{
        border: '1px solid #e0e0e0',
        borderRadius: '12px',
        p: 3,
        backgroundColor: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      }}>
        <Typography variant="caption" sx={{
          color: '#637381',
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          display: 'block',
          mb: 2
        }}>
          Select Pickup Location
        </Typography>

        {/* Pickup Location Selection */}
        {loadingPickupLocations ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress sx={{ color: 'var(--primary)' }} />
          </Box>
        ) : (
          <>
            {pickupLocations.length > 0 ? (
              <TextField
                select
                fullWidth
                required
                label="Choose Pickup Point"
                value={selectedPickupLocation}
                onChange={(e) => setSelectedPickupLocation(e.target.value)}
                error={!!formErrors.pickupLocation}
                helperText={formErrors.pickupLocation}
                size="small"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F4F6F8',
                    borderRadius: '8px',
                    '& fieldset': {
                      borderColor: formErrors.pickupLocation ? '#f44336' : '#E5E7EB',
                    },
                    '&:hover fieldset': {
                      borderColor: formErrors.pickupLocation ? '#f44336' : 'var(--primary)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: formErrors.pickupLocation ? '#f44336' : 'var(--primary)',
                    },
                  }
                }}
              >
                <MenuItem value="">
                  <em style={{ color: '#999' }}>Select a pickup location</em>
                </MenuItem>
                {pickupLocations.map((location) => (
                  <MenuItem key={location._id || location.id} value={location._id || location.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {location.locationName || location.name}
                      </Typography>
                      {location.address && (
                        <Typography variant="caption" color="text.secondary">
                          {location.address}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  No pickup locations available at the moment.
                </Typography>
              </Alert>
            )}

            {/* Selected Location Details */}
            {selectedPickupLocation && pickupLocations.length > 0 && (
              <Box sx={{
                p: 2,
                backgroundColor: '#F0FFFE',
                borderRadius: '8px',
                border: '1px solid #B0E2E0'
              }}>
                {(() => {
                  const selected = pickupLocations.find(loc => (loc._id || loc.id) === selectedPickupLocation);
                  if (!selected) return null;
                  return (
                    <>
                      <Typography variant="subtitle2" sx={{ color: 'var(--primary)', mb: 0.5, fontWeight: 600 }}>
                        <StoreIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                        {selected.locationName || selected.name}
                      </Typography>
                      {selected.address && (
                        <Typography variant="body2" sx={{ color: '#637381', mb: 0.5 }}>
                          {selected.address}
                        </Typography>
                      )}
                      {selected.workingHours && (
                        <Typography variant="caption" sx={{ color: '#637381' }}>
                          <strong>Store Hours:</strong> {selected.workingHours}
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{
                        display: 'block',
                        mt: 1,
                        color: 'var(--primary)',
                        fontWeight: 500
                      }}>
                        ✓ FREE pickup • Ready in 2-4 hours
                      </Typography>
                    </>
                  );
                })()}
              </Box>
            )}
          </>
        )}
      </Box>
    );
  };

  const renderPaymentForm = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Payment Method
      </Typography>

      {loadingPaymentMethods && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} sx={{ color: 'var(--primary)' }} />
        </Box>
      )}

      <RadioGroup
        value={paymentMethod}
        onChange={(e) => handlePaymentMethodChange(e.target.value)}
      >
        {/* M-Pesa Option - Only show if enabled */}
        {paymentMethodsStatus.mpesa && (
          <>
            <Card
              sx={{
                mb: 2,
                border: '2px solid',
                borderColor: paymentMethod === 'mpesa' ? 'var(--primary)' : '#e0e0e0',
                cursor: 'pointer',
                transition: 'all 0.2s',
                opacity: orderCreated && paymentMethod === 'mpesa' ? 1 : 1,
                '&:hover': {
                  borderColor: 'var(--primary)',
                  backgroundColor: 'color-mix(in srgb, var(--primary) 4%, transparent)',
                }
              }}
              onClick={() => handlePaymentMethodChange('mpesa')}
            >
              <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                <FormControlLabel
                  value="mpesa"
                  control={<Radio sx={{ color: 'var(--primary)', '&.Mui-checked': { color: 'var(--primary)' } }} />}
                  label={
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 0.5, sm: 1 }, ml: 1, width: '100%' }}>
                      <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>M-Pesa</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>(Most Popular)</Typography>
                      {orderCreated && paymentMethod === 'mpesa' && <Chip label="✓ Order Created" size="small" sx={{ ml: { xs: 0, sm: 1 }, bgcolor: '#4CAF50', color: 'white', fontSize: '0.65rem' }} />}
                    </Box>
                  }
                  sx={{ m: 0, width: '100%' }}
                />
              </CardContent>
            </Card>

            {paymentMethod === 'mpesa' && (
              <Box sx={{ mt: 2, mb: 2, p: { xs: 1.5, sm: 2 }, backgroundColor: '#f0fffe', borderRadius: 1, border: '1px solid #B0E2E0' }}>
                <TextField
                  fullWidth
                  label="M-Pesa Phone Number"
                  placeholder="2547XXXXXXXX"
                  value={mpesaPhoneNumber}
                  onChange={(e) => setMpesaPhoneNumber(e.target.value)}
                  helperText="Enter your M-Pesa registered phone number (format: 254XXXXXXXXX)"
                  error={!!formErrors.mpesaPhone}
                  size="small"
                  disabled={isPaymentProcessing}
                  sx={{ '& .MuiFormHelperText-root': { fontSize: { xs: '0.7rem', sm: '0.75rem' } } }}
                />
                <Box sx={{ mt: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 }, alignItems: { xs: 'stretch', sm: 'center' } }}>
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: '#15B7A1',
                      color: 'white',
                      flex: { xs: 'none', sm: 1 },
                      order: { xs: 1, sm: 0 },
                      '&:hover': { backgroundColor: '#0f9b87' },
                      '&:disabled': { backgroundColor: '#ccc' },
                      fontSize: { xs: '0.85rem', sm: '1rem' },
                      padding: { xs: '8px 12px', sm: '10px 16px' }
                    }}
                    onClick={() => handleInitiateStkPayment('mpesa')}
                    disabled={!mpesaPhoneNumber.trim() || isPaymentProcessing || paymentSuccessful}
                  >
                    {isPaymentProcessing ? (
                      <>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        Processing...
                      </>
                    ) : paymentSuccessful ? (
                      <>
                        <CheckCircle sx={{ mr: 1, fontSize: { xs: '18px', sm: '20px' } }} />
                        Payment Successful
                      </>
                    ) : (
                      'Pay Now'
                    )}
                  </Button>
                  {paymentSuccessful && (
                    <Chip
                      label="✓ Payment Successful"
                      size="small"
                      sx={{ bgcolor: '#4CAF50', color: 'white', order: { xs: 0, sm: 1 }, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                  You will receive an STK push notification to complete the payment.
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* Card Option - Only show if enabled */}
        {paymentMethodsStatus.card && (
          <>
            <Card
              sx={{
                mb: 2,
                border: '2px solid',
                borderColor: paymentMethod === 'card' ? 'var(--primary)' : '#e0e0e0',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'var(--primary)',
                  backgroundColor: 'color-mix(in srgb, var(--primary) 4%, transparent)',
                }
              }}
              onClick={() => handlePaymentMethodChange('card')}
            >
              <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                <FormControlLabel
                  value="card"
                  control={<Radio sx={{ color: 'var(--primary)', '&.Mui-checked': { color: 'var(--primary)' } }} />}
                  label={
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 0.5, sm: 1 }, ml: 1, width: '100%' }}>
                      <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>Credit/Debit Card</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>(Visa, Mastercard, Amex)</Typography>
                      {orderCreated && paymentMethod === 'card' && <Chip label="✓ Order Created" size="small" sx={{ ml: { xs: 0, sm: 1 }, bgcolor: '#4CAF50', color: 'white', fontSize: '0.65rem' }} />}
                    </Box>
                  }
                  sx={{ m: 0, width: '100%' }}
                />
              </CardContent>
            </Card>

            {paymentMethod === 'card' && (
              <Box sx={{ mt: 2, mb: 2 }}>
                {orderCreated ? (
                  <Box sx={{ p: 2.5, backgroundColor: '#f0fffe', borderRadius: 1, border: '1px solid #B0E2E0' }}>
                    <PaymentForm
                      amount={orderCreatedDetails?.transamount}
                      invoiceBalance={orderCreatedDetails?.transamount}
                      type="order"
                      reference={orderCreatedDetails?._id}
                      businessNumber={orderCreatedDetails?.businessId}
                      isBilling={false}
                      onPaymentResponse={handleCardPaymentResponse}
                      setLoading={setIsCardPaymentLoading}
                      disableTermlyFee={true}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                      💳 Your card information will be encrypted and processed securely using industry-standard encryption.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ p: 2.5, backgroundColor: '#f0fffe', borderRadius: 1, border: '1px solid #B0E2E0' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Card Number"
                          placeholder="1234 5678 9012 3456"
                          size="small"
                          disabled
                          InputProps={{
                            style: { color: '#999' }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Cardholder Name"
                          placeholder="John Doe"
                          size="small"
                          disabled
                          InputProps={{
                            style: { color: '#999' }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          fullWidth
                          label="Expiry (MM/YY)"
                          placeholder="12/25"
                          size="small"
                          disabled
                          InputProps={{
                            style: { color: '#999' }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          fullWidth
                          label="CVV"
                          placeholder="123"
                          type="password"
                          size="small"
                          disabled
                          InputProps={{
                            style: { color: '#999' }
                          }}
                        />
                      </Grid>
                    </Grid>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                      💳 Click "Next" to review your order. Secure card fields will be activated to collect your payment details.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}

        {/* Mobile Money / Paybill Option - Only show if enabled */}
        {paymentMethodsStatus.paybill && (
          <>
            <Card
              sx={{
                mb: 2,
                border: '2px solid',
                borderColor: paymentMethod === 'kcbpaybill' ? 'var(--primary)' : '#e0e0e0',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'var(--primary)',
                  backgroundColor: 'color-mix(in srgb, var(--primary) 4%, transparent)',
                }
              }}
              onClick={() => handlePaymentMethodChange('kcbpaybill')}
            >
              <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                <FormControlLabel
                  value="kcbpaybill"
                  control={<Radio sx={{ color: 'var(--primary)', '&.Mui-checked': { color: 'var(--primary)' } }} />}
                  label={
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 0.5, sm: 1 }, ml: 1, width: '100%' }}>
                      <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>KCB Paybill</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>/ Mobile Money</Typography>
                      {orderCreated && paymentMethod === 'kcbpaybill' && <Chip label="✓ Order Created" size="small" sx={{ ml: { xs: 0, sm: 1 }, bgcolor: '#4CAF50', color: 'white', fontSize: '0.65rem' }} />}
                    </Box>
                  }
                  sx={{ m: 0, width: '100%' }}
                />
              </CardContent>
            </Card>

            {paymentMethod === 'kcbpaybill' && (
              <Box sx={{ mt: 2, mb: 2, p: { xs: 1.5, sm: 2 }, backgroundColor: '#f0fffe', borderRadius: 1, border: '1px solid #B0E2E0' }}>
                <TextField
                  fullWidth
                  label="Mobile Money Phone Number"
                  placeholder="2547XXXXXXXX"
                  value={mobileMoneyNumber}
                  onChange={(e) => setMobileMoneyNumber(e.target.value)}
                  helperText="Enter your Mobile Money phone number"
                  size="small"
                  disabled={isPaymentProcessing}
                  sx={{ '& .MuiFormHelperText-root': { fontSize: { xs: '0.7rem', sm: '0.75rem' } } }}
                />
                <Box sx={{ mt: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 }, alignItems: { xs: 'stretch', sm: 'center' } }}>
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: '#15B7A1',
                      color: 'white',
                      flex: { xs: 'none', sm: 1 },
                      order: { xs: 1, sm: 0 },
                      '&:hover': { backgroundColor: '#0f9b87' },
                      '&:disabled': { backgroundColor: '#ccc' },
                      fontSize: { xs: '0.85rem', sm: '1rem' },
                      padding: { xs: '8px 12px', sm: '10px 16px' }
                    }}
                    onClick={() => handleInitiateStkPayment('kcbpaybill')}
                    disabled={!mobileMoneyNumber.trim() || isPaymentProcessing || paymentSuccessful}
                  >
                    {isPaymentProcessing ? (
                      <>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        Processing...
                      </>
                    ) : paymentSuccessful ? (
                      <>
                        <CheckCircle sx={{ mr: 1, fontSize: { xs: '18px', sm: '20px' } }} />
                        Payment Successful
                      </>
                    ) : (
                      'Pay Now'
                    )}
                  </Button>
                  {paymentSuccessful && (
                    <Chip
                      label="✓ Payment Successful"
                      size="small"
                      sx={{ bgcolor: '#4CAF50', color: 'white', order: { xs: 0, sm: 1 }, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                  STK push will be sent to your phone. Please enter your M-Pesa PIN to complete the payment.
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* Show message if no payment methods available */}
        {!paymentMethodsStatus.mpesa && !paymentMethodsStatus.card && !paymentMethodsStatus.paybill && (
          <Alert severity="warning">
            No payment methods are currently available. Please try again later.
          </Alert>
        )}
      </RadioGroup>

      {paymentMethod === 'bank-transfer' && (
        <Box sx={{ mt: 3, p: 2, backgroundColor: '#f8f8f8', borderRadius: 1 }}>
          <Typography variant="body1" gutterBottom fontWeight="500">
            Bank Transfer Details:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Bank: Kenya Commercial Bank (KCB)<br />
            Account Name: {businessName || 'N/A'}<br />
            Account Number: 1234567890<br />
            Branch: Westlands<br />
            Swift Code: KCBLKENX
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Please use your order number as the reference when making the transfer.
          </Typography>
        </Box>
      )}

      {paymentMethod === 'evoucher' && (
        <Box sx={{ mt: 3, p: 2, backgroundColor: '#f8f8f8', borderRadius: 1 }}>
          <Typography variant="body1" gutterBottom fontWeight="500">
            eVoucher Code Redemption:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Enter eVoucher Code"
                placeholder="e.g., EVOUCHER2024ABC123"
                helperText="Enter your valid eVoucher code to redeem"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                sx={{
                  borderColor: 'var(--primary)',
                  color: 'var(--primary)',
                  '&:hover': {
                    borderColor: 'var(--button-hover)',
                    backgroundColor: 'color-mix(in srgb, var(--primary) 8%, transparent)',
                  },
                }}
              >
                Validate Code
              </Button>
            </Grid>
          </Grid>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            eVoucher codes can be used to pay partially or fully for your order.
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderOrderReview = () => {
    // Use service to calculate totals from cart
    const cartTotals = checkoutService.calculateOrderTotals(cartItems, deliveryFee);

    // Use order totals if order was created
    const orderAmount = orderCreatedDetails?.transamount || cartTotals.total;

    return (
      <Box>
        {/* Payment Confirmation Details - Show after successful payment */}
        {paymentSuccessful && paymentStatusDetails && (
          <Box sx={{ mb: 3 }}>
            {/* Show loading state while fetching */}
            {loadingPaymentStatus && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress size={40} sx={{ color: 'var(--primary)', mb: 2 }} />
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Loading order details...
                </Typography>
              </Box>
            )}

            {/* Success Header */}
            <Box sx={{
              mb: 2,
              p: 2.5,
              backgroundColor: 'color-mix(in srgb, var(--primary) 8%, transparent)',
              borderRadius: '8px',
              border: '2px solid var(--primary)',
              textAlign: 'center'
            }}>
              <Typography variant="h6" sx={{ color: 'var(--primary)', fontWeight: 700, mb: 0.5 }}>
                ✓ Payment Confirmed
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Your payment has been successfully processed
              </Typography>
            </Box>

            {/* Payment & Order Details Section */}
            <Box sx={{
              p: 2.5,
              backgroundColor: '#fff',
              borderRadius: '8px',
              border: '1px solid #e0e0e0',
              mb: 2
            }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#212B36', mb: 2 }}>
                Payment & Order Details
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                <Box sx={{ pb: 2, borderBottom: { xs: '1px solid #f0f0f0', sm: 'none' } }}>
                  <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Order Number
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#212B36', fontWeight: 600, mt: 0.5 }}>
                    {getPaymentStatusData('orderNumber') || getPaymentStatusData('orderNumberSequence')}
                  </Typography>
                </Box>

                <Box sx={{ pb: 2, borderBottom: { xs: '1px solid #f0f0f0', sm: 'none' } }}>
                  <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Order Status
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1976D2', fontWeight: 600, mt: 0.5 }}>
                    {getPaymentStatusData('orderStatus', 'Processing')}
                  </Typography>
                </Box>

                <Box sx={{ pb: 2, borderBottom: { xs: '1px solid #f0f0f0', sm: 'none' } }}>
                  <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Customer Name
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#212B36', fontWeight: 600, mt: 0.5 }}>
                    {getPaymentStatusData('customerName')}
                  </Typography>
                </Box>

                <Box sx={{ pb: 2, borderBottom: { xs: '1px solid #f0f0f0', sm: 'none' } }}>
                  <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Amount Paid
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'var(--primary)', fontWeight: 700, mt: 0.5, fontSize: '1.1rem' }}>
                    KSH {((getPaymentStatusData('transamount') || orderAmount || 0) * 1).toLocaleString()}
                  </Typography>
                </Box>

                <Box sx={{ pb: 2, borderBottom: { xs: '1px solid #f0f0f0', sm: 'none' } }}>
                  <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Payment Method
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#212B36', fontWeight: 600, mt: 0.5 }}>
                    {getPaymentStatusData('paymentMethod', paymentMethod === 'mpesa' ? 'M-Pesa' : paymentMethod === 'card' ? 'Credit/Debit Card' : paymentMethod === 'kcbpaybill' ? 'KCB Paybill/Mobile Money' : 'Unknown')}
                  </Typography>
                </Box>

                <Box sx={{ pb: 2, borderBottom: { xs: '1px solid #f0f0f0', sm: 'none' } }}>
                  <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Payment Status
                  </Typography>
                  <Chip
                    label={getPaymentStatusData('paid', false) ? 'PAID' : 'PENDING'}
                    size="small"
                    sx={{
                      mt: 0.5,
                      backgroundColor: getPaymentStatusData('paid', false) ? 'color-mix(in srgb, var(--primary) 12%, transparent)' : 'rgba(255, 152, 0, 0.12)',
                      color: getPaymentStatusData('paid', false) ? 'var(--primary)' : '#F57C00',
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}
                  />
                </Box>

                <Box sx={{ pb: 2, borderBottom: { xs: '1px solid #f0f0f0', sm: 'none' } }}>
                  <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Transaction ID
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#212B36', fontWeight: 600, mt: 0.5, fontSize: '0.8rem', wordBreak: 'break-all' }}>
                    {getPaymentStatusData('stkOrderId') || getPaymentStatusData('pushTransactionId')}
                  </Typography>
                </Box>

                <Box sx={{ pb: 2, borderBottom: { xs: '1px solid #f0f0f0', sm: 'none' } }}>
                  <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Receipt ID
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#212B36', fontWeight: 600, mt: 0.5, fontSize: '0.8rem', wordBreak: 'break-all' }}>
                    {(paymentStatusDetails?.transactionsList?.[0]?.receiptId) || getPaymentStatusData('transactionId')}
                  </Typography>
                </Box>

                <Box sx={{ pb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Payment Date & Time
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#212B36', fontWeight: 500, mt: 0.5 }}>
                    {getPaymentStatusData('transtime') ? (
                      <>
                        {new Date(getPaymentStatusData('transtime')).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })} at {new Date(getPaymentStatusData('transtime')).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </>
                    ) : (
                      'N/A'
                    )}
                  </Typography>
                </Box>

                {/* STK Phone Number - if available */}
                {getPaymentStatusData('stkPhone') && (
                  <Box sx={{ pb: 2, borderBottom: { xs: '1px solid #f0f0f0', sm: 'none' } }}>
                    <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                      STK Phone Number
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#212B36', fontWeight: 600, mt: 0.5 }}>
                      {getPaymentStatusData('stkPhone')}
                    </Typography>
                  </Box>
                )}

                {/* Customer Email - if available */}
                {getPaymentStatusData('customerEmail') && (
                  <Box sx={{ pb: 2, borderBottom: { xs: '1px solid #f0f0f0', sm: 'none' } }}>
                    <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                      Email
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#212B36', fontWeight: 600, mt: 0.5, wordBreak: 'break-all' }}>
                      {getPaymentStatusData('customerEmail')}
                    </Typography>
                  </Box>
                )}

                {/* Customer Phone - if available */}
                {getPaymentStatusData('customerPhone') && (
                  <Box sx={{ pb: 2, borderBottom: { xs: '1px solid #f0f0f0', sm: 'none' } }}>
                    <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                      Phone Number
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#212B36', fontWeight: 600, mt: 0.5, wordBreak: 'break-all' }}>
                      {getPaymentStatusData('customerPhone')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Order Summary Section */}
            <Box sx={{
              p: 2.5,
              backgroundColor: '#fff',
              borderRadius: '8px',
              border: '1px solid #e0e0e0',
              mb: 2
            }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#212B36', mb: 2 }}>
                Order Summary
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Subtotal
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#212B36', fontWeight: 600, mt: 0.5 }}>
                    KSH {((getPaymentStatusData('subTotal') || 0) * 1).toLocaleString('en-US', { maximumFractionDigits: 2 })}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    VAT (16%)
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#212B36', fontWeight: 600, mt: 0.5 }}>
                    KSH {((getPaymentStatusData('vat') || 0) * 1).toLocaleString('en-US', { maximumFractionDigits: 2 })}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Shipping Cost
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#212B36', fontWeight: 600, mt: 0.5 }}>
                    {(getPaymentStatusData('shippingAmount') || 0) * 1 === 0 ? 'FREE' : `KSH ${((getPaymentStatusData('shippingAmount') || 0) * 1).toLocaleString()}`}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Discount
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#212B36', fontWeight: 600, mt: 0.5 }}>
                    KSH {((getPaymentStatusData('discountAmount') || 0) * 1).toLocaleString('en-US', { maximumFractionDigits: 2 })}
                  </Typography>
                </Box>

                <Box sx={{ gridColumn: { xs: 'auto', sm: '1 / 3' }, borderTop: '2px solid #f0f0f0', pt: 1.5 }}>
                  <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Total Amount
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'var(--primary)', fontWeight: 700, mt: 0.5, fontSize: '1.2rem' }}>
                    KSH {((getPaymentStatusData('transamount') || orderAmount || 0) * 1).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Delivery Information */}
            {(getPaymentStatusData('deliveryMethod') || getPaymentStatusData('pickUpLocationDetails')) && (
              <Box sx={{
                p: 2.5,
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                mb: 2
              }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#212B36', mb: 2 }}>
                  Delivery Information
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                      Delivery Method
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#212B36', fontWeight: 500, mt: 0.5 }}>
                      {getPaymentStatusData('deliveryMethod') === 'pickup' ? '📍 Pickup' : '🚚 Delivery'}
                    </Typography>
                  </Box>

                  {getPaymentStatusData('deliveryMethod') === 'pickup' && getPaymentStatusData('pickUpLocationDetails') && (
                    <Box>
                      <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                        Pickup Location
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#212B36', fontWeight: 500, mt: 0.5 }}>
                        {typeof getPaymentStatusData('pickUpLocationDetails') === 'object' ? getPaymentStatusData('pickUpLocationDetails')?.locationName : getPaymentStatusData('pickUpLocationDetails')}
                      </Typography>
                    </Box>
                  )}

                  {getPaymentStatusData('deliveryMethod') === 'delivery' && getPaymentStatusData('shippingCustomerpreference') && (
                    <>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                          Address
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#212B36', fontWeight: 500, mt: 0.5 }}>
                          {typeof getPaymentStatusData('shippingCustomerpreference') === 'object' ? getPaymentStatusData('shippingCustomerpreference')?.address : getPaymentStatusData('shippingCustomerpreference')}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                          City
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#212B36', fontWeight: 500, mt: 0.5 }}>
                          {typeof getPaymentStatusData('shippingCustomerpreference') === 'object' ? getPaymentStatusData('shippingCustomerpreference')?.city : 'N/A'}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            )}

            {/* Order Items */}
            {(getPaymentStatusData('items') || (paymentStatusDetails?.order?.items)) && (
              <Box sx={{
                p: 2.5,
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #e0e0e0'
              }}>
                {(() => {
                  const items = getPaymentStatusData('items') || (paymentStatusDetails?.order?.items) || [];
                  if (!Array.isArray(items) || items.length === 0) {
                    return <Typography variant="body2" sx={{ color: '#999' }}>No items to display</Typography>;
                  }

                  return (
                    <>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#212B36', mb: 2 }}>
                        Order Items ({items.length})
                      </Typography>

                      {items.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            py: 1.5,
                            borderBottom: index < items.length - 1 ? '1px solid #f0f0f0' : 'none'
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#212B36', mb: 0.5 }}>
                              {item.itemName || item.productName || item.name || 'Product'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#999' }}>
                              Qty: {item.itemCount || item.quantity || 1} · Category: {item.itemCategory || item.category || 'N/A'}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--primary)', ml: 2, textAlign: 'right' }}>
                            KSH {((item.totalAmount || (item.itemAmount * (item.itemCount || item.quantity || 1))) || 0).toLocaleString()}
                          </Typography>
                        </Box>
                      ))}
                    </>
                  );
                })()}
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderDeliveryForm();
      case 1:
        return renderPaymentForm();
      case 2:
        return renderOrderReview();
      default:
        return 'Unknown step';
    }
  };

  if (cartItems.length === 0) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh',
          background: `
            linear-gradient(135deg, color-mix(in srgb, var(--primary) 3%, transparent) 25%, transparent 25%),
            linear-gradient(225deg, color-mix(in srgb, var(--primary) 3%, transparent) 25%, transparent 25%),
            linear-gradient(45deg, color-mix(in srgb, var(--primary) 3%, transparent) 25%, transparent 25%),
            linear-gradient(315deg, color-mix(in srgb, var(--primary) 3%, transparent) 25%, transparent 25%)
          `,
          backgroundPosition: '20px 0, 20px 0, 0 0, 0 0',
          backgroundSize: '40px 40px',
          backgroundRepeat: 'repeat',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pt: 8,
          pb: 8,
        }}
      >
        <Seo title="Checkout" noindex />
        <Container maxWidth="sm">
          <Box sx={{ textAlign: 'center' }}>
            {/* Enhanced Empty Cart Icon */}
            <Box
              sx={{
                width: 180,
                height: 180,
                mx: 'auto',
                mb: 4,
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Outer Circle with Gradient */}
              <Box
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, color-mix(in srgb, var(--primary) 10%, transparent) 0%, color-mix(in srgb, var(--primary) 5%, transparent) 100%)',
                  animation: 'pulse 3s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': {
                      transform: 'scale(1)',
                      opacity: 0.5,
                    },
                    '50%': {
                      transform: 'scale(1.05)',
                      opacity: 0.8,
                    },
                  },
                }}
              />
              
              {/* Inner Circle */}
              <Box
                sx={{
                  position: 'absolute',
                  width: 140,
                  height: 140,
                  borderRadius: '50%',
                  backgroundColor: 'color-mix(in srgb, var(--primary) 8%, transparent)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ShoppingCartIcon 
                  sx={{ 
                    fontSize: 70, 
                    color: 'var(--primary)',
                    opacity: 0.9,
                  }} 
                />
              </Box>
              
              {/* Decorative Dots */}
              <Box
                sx={{
                  position: 'absolute',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: 'var(--primary)',
                  top: 20,
                  right: 30,
                  opacity: 0.6,
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: 'var(--primary)',
                  bottom: 25,
                  left: 25,
                  opacity: 0.4,
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: 'var(--primary)',
                  top: 50,
                  left: 10,
                  opacity: 0.3,
                }}
              />
            </Box>
            
            <Typography 
              variant="h4" 
              gutterBottom 
              sx={{ 
                fontWeight: 700, 
                color: '#1a1a1a',
                mb: 2,
                fontSize: { xs: '1.8rem', sm: '2.125rem' },
              }}
            >
              Your cart is empty
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 4, 
                maxWidth: 450, 
                mx: 'auto',
                color: '#666',
                fontSize: '1.05rem',
                lineHeight: 1.6,
              }}
            >
              You haven't added any items to your cart yet. Start shopping to find your perfect sports jersey!
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={() => navigate('/products')}
                size="large"
                startIcon={<ShoppingBagIcon />}
                sx={{
                  backgroundColor: 'var(--button-primary)',
                  px: 4,
                  py: 1.5,
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  '&:hover': { 
                    backgroundColor: 'var(--button-hover)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px color-mix(in srgb, var(--primary) 30%, transparent)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Start Shopping
              </Button>
              
              <Button
                variant="outlined"
                onClick={() => navigate('/')}
                size="large"
                sx={{
                  borderColor: 'var(--primary)',
                  color: 'var(--primary)',
                  px: 4,
                  py: 1.5,
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  '&:hover': { 
                    borderColor: 'var(--button-hover)',
                    backgroundColor: 'color-mix(in srgb, var(--primary) 8%, transparent)',
                  },
                }}
              >
                Go Home
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 4 } }}>
      <Seo title="Checkout" noindex />
      <Typography variant="h3" component="h1" fontWeight="bold" sx={{ mb: 2, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
        Checkout
      </Typography>
      
      {deliveryMethod === 'ship' && selectedShippingRegion && (() => {
        const selectedRegionForBanner = shippingRegions.find(r => r._id === selectedShippingRegion);
        if (!selectedRegionForBanner) return null;
        return (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Delivery to <strong>{selectedRegionForBanner.regionName}</strong>{selectedArea && `, ${selectedArea}`} - KSH {deliveryFee.toLocaleString()} delivery fee
            </Typography>
          </Alert>
        );
      })()}

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel 
              sx={{
                '& .MuiStepIcon-root': {
                  '&.Mui-completed': { color: 'var(--primary)' },
                  '&.Mui-active': { color: 'var(--primary)' },
                }
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Success Alert */}
      {successAlert.show && (
        <Alert severity="success" onClose={() => setSuccessAlert({ show: false, message: '' })} sx={{ mb: 2 }}>
          {successAlert.message}
        </Alert>
      )}

      {/* Error Alert */}
      {errorAlert.show && (
        <Alert severity="error" onClose={() => setErrorAlert({ show: false, message: '' })} sx={{ mb: 2 }}>
          {errorAlert.message}
        </Alert>
      )}

      <Card>
        <CardContent>
          {getStepContent(activeStep)}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0 || activeStep === steps.length - 1} // Disable back button on first and last step
              onClick={handleBack}
            >
              Back
            </Button>
            {activeStep === steps.length - 1 ? (
              // On review step
              paymentSuccessful ? (
                // If payment successful, show View My Orders button
                <Button
                  variant="contained"
                  onClick={() => {
                    // Clear cart before navigating away
                    dispatch(clearCart());
                    cartApi.clearCart();
                    navigate('/account?tab=orders');
                  }}
                  size="large"
                  sx={{
                    backgroundColor: 'var(--button-primary)',
                    color: 'white',
                    '&:hover': { backgroundColor: 'var(--button-hover)' }
                  }}
                >
                  View My Orders
                </Button>
              ) : (
                // If payment not successful yet, show Place Order button
                <Button
                  variant="contained"
                  onClick={handlePlaceOrder}
                  size="large"
                  disabled={isCardPaymentLoading || openOrderProcessing}
                >
                  {isCardPaymentLoading ? 'Processing Payment...' : 'Place Order'}
                </Button>
              )
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                size="large"
                disabled={
                  // Disable Next button on payment method step (step 1) with STK methods until payment successful
                  activeStep === 1 && (paymentMethod === 'mpesa' || paymentMethod === 'kcbpaybill') && !paymentSuccessful
                }
              >
                Next
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Order Processing Modal */}
      <Modal
        open={openOrderProcessing}
        onClose={() => {}}
        disableEscapeKeyDown
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#fff',
            borderRadius: '12px',
            boxShadow: 3,
            p: 4,
            textAlign: 'center',
            minWidth: 300,
          }}
        >
          <Box
            component="img"
            src={loadingIcon}
            alt="Loading"
            sx={{
              width: 100,
              height: 100,
              mx: 'auto',
              mb: 2,
              animation: 'spin 2s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          />
          <Typography variant="body1" sx={{ color: '#212B36', mb: 1 }}>
            Processing Your Payment
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            {paymentMethod === 'card'
              ? 'You will be redirected to the secure payment gateway.'
              : 'Please check your phone for the STK push prompt.'}
          </Typography>
        </Box>
      </Modal>
    </Container>
  );
};

export default Checkout;